import { STAGE } from "@/config/defaultSettings";
import { isFeatureEnabled as evaluateFeatureFlag } from "@/utils/featureFlags";

/**
 * Attune docs host contract.
 * Keep this centralized so all help-hub links resolve consistently.
 */
export const DOCS_ORIGIN = "https://docs.attunelogic.com";

export const DOCS_PLUGIN_IDS = ["customer", "developer", "proposals"];

/** Docs trees that only superAdmins may open. */
export const STAFF_DOCS_PLUGIN_IDS = Object.freeze([
  "developer",
  "proposals",
  "plans",
]);

export const DOCS_STAFF_MESSAGE_TYPE = "attune-docs-staff";

export const DOCS_FALLBACK_BY_PLUGIN = {
  customer: "intro",
  developer: "intro",
  proposals: "intro",
};

export function buildDocsUrl({ docsPluginId, docsSlug }) {
  const pluginId = DOCS_PLUGIN_IDS.includes(docsPluginId)
    ? docsPluginId
    : "customer";
  const slug = docsSlug || DOCS_FALLBACK_BY_PLUGIN[pluginId];
  const normalizedSlug = String(slug || "").replace(/^\/+|\/+$/g, "");
  return `${DOCS_ORIGIN}/${pluginId}/${normalizedSlug}`;
}

/**
 * Customer docs home — the searchable docs library users can open from the app.
 * @returns {string}
 */
export function buildDocsLibraryUrl() {
  return `${DOCS_ORIGIN}/customer/intro`;
}

/**
 * Docs-site search (Lunr) for a query users typed in the app.
 * @param {string} [query]
 * @returns {string}
 */
export function buildDocsSearchUrl(query) {
  const q = String(query || "").trim();
  if (!q) return `${DOCS_ORIGIN}/search`;
  return `${DOCS_ORIGIN}/search?q=${encodeURIComponent(q)}`;
}

/**
 * @param {string} [pluginId]
 * @returns {boolean}
 */
export function isStaffDocsPlugin(pluginId) {
  return STAFF_DOCS_PLUGIN_IDS.includes(pluginId);
}

/**
 * @param {{ authority?: string }|null|undefined} user
 * @returns {boolean}
 */
export function canAccessStaffDocs(user) {
  return user?.authority === "superAdmin";
}

/**
 * Whether this user may open a published docs URL for the plugin.
 * Customer docs require an Attune sign-in on the docs site.
 * Developer / proposals / plans require superAdmin.
 * @param {string} [docsPluginId]
 * @param {{ authority?: string }|null|undefined} user
 * @returns {boolean}
 */
export function canOpenDocsPlugin(docsPluginId, user) {
  if (!isStaffDocsPlugin(docsPluginId)) return true;
  return canAccessStaffDocs(user);
}

/**
 * Whether a docs-site origin may receive the staff-access postMessage.
 * @param {string} [origin]
 * @returns {boolean}
 */
export function isAllowedDocsOrigin(origin) {
  if (!origin) return false;
  try {
    const url = new URL(origin);
    if (url.protocol === "https:" && url.hostname === "docs.attunelogic.com") {
      return true;
    }
    if (url.hostname === "localhost" || url.hostname === "127.0.0.1") {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Origin of the docs site that embedded this page, if any.
 * `ancestorOrigins` is missing in Firefox; `document.referrer` covers that case.
 * @returns {string}
 */
export function getParentFrameOrigin() {
  if (typeof window === "undefined") return "";
  const ancestor = window.location.ancestorOrigins?.[0];
  if (ancestor) return ancestor;
  const referrer = document.referrer;
  if (!referrer) return "";
  try {
    return new URL(referrer).origin;
  } catch {
    return "";
  }
}

/**
 * Whether a `next` URL from the docs handoff may be used as a redirect target.
 * @param {string} [raw]
 * @returns {boolean}
 */
export function isAllowedDocsReturnUrl(raw) {
  if (!raw) return false;
  try {
    return isAllowedDocsOrigin(new URL(raw).origin);
  } catch {
    return false;
  }
}

export function isFeatureEnabled(featureFlags, featureKey) {
  return evaluateFeatureFlag(featureFlags, featureKey);
}

export function isFeatureAvailableForAppType(appTypes, appType) {
  if (!Array.isArray(appTypes) || appTypes.length === 0) return true;
  if (!appType) return true;
  return appTypes.includes(appType);
}

export const docsContract = {
  stage: STAGE,
  docsOrigin: DOCS_ORIGIN,
  pluginIds: DOCS_PLUGIN_IDS,
  fallbackByPlugin: DOCS_FALLBACK_BY_PLUGIN,
  buildDocsUrl,
  buildDocsLibraryUrl,
  buildDocsSearchUrl,
  isStaffDocsPlugin,
  canAccessStaffDocs,
  canOpenDocsPlugin,
  isAllowedDocsOrigin,
  getParentFrameOrigin,
  isAllowedDocsReturnUrl,
};

export default docsContract;
